import { NodeLayout } from '@/components/nodes/layout';
import { handleError } from '@/lib/error/handle';
import { uploadFile } from '@/lib/upload';
import { useAuth, useUser } from '@clerk/nextjs';
import { useReactFlow } from '@xyflow/react';
import { FileIcon, FolderOpen, Loader2Icon, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { useI18n } from '@/providers/i18n';
import { ReuploadButton } from '@/components/nodes/shared/reupload-button';
import { NodeSurface } from '@/components/nodes/shared';
import type { FileNodeProps } from '.';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { Node } from '@xyflow/react';
import { FileCollapsedSummary } from '@/components/nodes/file/collapsed-summary';
import { useProject } from '@/providers/project';
import { requestLibraryPick, type LibraryPickResult } from '@/components/canvas/utils/asset-folders-events';

type FilePrimitiveProps = FileNodeProps & {
  title: string;
};

const EMPTY_MENU_ITEM_CN =
  'flex items-center gap-4 px-4 py-3.5 text-sm text-foreground/80 hover:bg-sidebar-accent/40 active:bg-sidebar-accent/60 transition-colors rounded-xl';

const FilePreview = ({
  name,
  type,
  url,
}: { name: string; type: string; url: string }) => {
  const { t } = useI18n();
  return (
  <div className="flex flex-col gap-2">
    <div className="flex items-center gap-2">
      <FileIcon size={16} className="text-muted-foreground" />
      <span className="truncate font-medium text-sm">{name}</span>
    </div>
    {type === 'application/pdf' ? (
      <div className="relative aspect-[3/4] w-full overflow-hidden rounded border">
        <iframe
          src={`${url}#view=FitH`}
          className="absolute inset-0 h-full w-full"
          title={t("PDF Preview")}
        />
      </div>
    ) : (
      <div className="flex items-center justify-center rounded border p-4">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-primary text-sm hover:underline"
        >
          <FileIcon size={16} />
          <span>Download File</span>
        </a>
      </div>
    )}
  </div>
  );
};

export const FilePrimitive = ({
  data,
  id,
  type,
  title,
}: FilePrimitiveProps) => {
  const { t } = useI18n();
  const { updateNodeData, getNodes, getEdges, updateNode } = useReactFlow();
  const project = useProject();
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { getToken } = useAuth();
  const { user } = useUser();
  const contentUrl = data.content?.url;
  const hasContent = Boolean(data.text || contentUrl);
  const isSafetensors =
    !!data.content?.name &&
    data.content.name.toLowerCase().endsWith('.safetensors');

  // 写入lora信息到已连接的图像节点
  const writeLoraToConnectedImageNodes = (fileUrl: string, fileName: string, scale: number) => {
    try {
      const nodesArr = getNodes();
      const edgesArr = getEdges();
      
      // 找到所有从当前文件节点连接到图像节点的边
      const connectedImageNodes = edgesArr
        .filter((e) => String(e.source) === String(id) && e.target)
        .map((e) => {
          const targetNode = nodesArr.find((n) => String(n.id) === String(e.target));
          return targetNode?.type === 'image' ? targetNode : null;
        })
        .filter((n): n is Node => n !== null);

      // 对每个连接的图像节点写入lora信息
      for (const imageNode of connectedImageNodes) {
        const isSafetensorsFile = /\.safetensors$/i.test(fileName);
        const isZipFile = /\.zip$/i.test(fileName);
        
        if (isSafetensorsFile && fileUrl) {
          const nodeData = (imageNode as any).data || {};
          const modelUi = { ...(nodeData.modelUi || {}) } as Record<string, any>;
          const candidates = ['loras1', 'loras2', 'loras3'];
          // 优先填充空位；若无空位但已存在相同 URL，则更新对应 scale；否则覆盖 loras1
          let assignedKey = candidates.find((k) => {
            const v = modelUi[k];
            return typeof v !== 'string' || v.trim().length === 0;
          });
          if (!assignedKey) {
            const sameKey = candidates.find((k) => typeof modelUi[k] === 'string' && String(modelUi[k]).trim() === fileUrl);
            assignedKey = sameKey || 'loras1';
          }
          const nextModelUi = {
            ...modelUi,
            [assignedKey]: fileUrl,
            [`${assignedKey}_scale`]: scale,
          };
          updateNode(imageNode.id, { data: { ...nodeData, modelUi: nextModelUi } });
        } else if (isZipFile && fileUrl) {
          // 训练/数据集 ZIP：将文件直链写入 modelUi.file_url，供 providerOptions 透传
          const nodeData = (imageNode as any).data || {};
          const modelUi = { ...(nodeData.modelUi || {}) } as Record<string, any>;
          const nextModelUi = { ...modelUi, file_url: fileUrl };
          updateNode(imageNode.id, { data: { ...nodeData, modelUi: nextModelUi } });
        }
      }
    } catch (error) {
      // 静默失败，不影响文件上传流程
      console.error('Error writing lora to connected image nodes:', error);
    }
  };

  const handleDrop = async (files: File[]) => {
    if (isUploading) {
      return;
    }

    try {
      if (!files.length) {
        throw new Error('No file selected');
      }

      if (files.some((file) => file.type.startsWith('audio'))) {
        throw new Error('Please use the audio node to upload audio files.');
      }

      if (files.some((file) => file.type.startsWith('video'))) {
        throw new Error('Please use the video node to upload video files.');
      }

      if (files.some((file) => file.type.startsWith('image'))) {
        throw new Error('Please use the image node to upload image files.');
      }

      setIsUploading(true);
      const [file] = files;

      // 如果是 JSON 文件，读取内容并解析
      const isJson =
        file.type === 'application/json' || /\.json$/i.test(file.name);
      if (isJson) {
        const text = await file.text();
        try {
          // 验证是否为有效 JSON
          JSON.parse(text);
          // 写入 text 字段和文件名，不写入 content
          updateNodeData(id, {
            text,
            fileName: file.name,
            content: undefined,
            scale: undefined,
          });
        } catch (parseError) {
          throw new Error('Invalid JSON file');
        }
      } else {
        // 非 JSON 文件，保持原有逻辑
        if (!project?.id) throw new Error('Missing project');
        const token = await getToken({ template: 'supabase' });
        if (!token) throw new Error('No Clerk token');
        if (!user?.id) throw new Error('No Clerk user');
        const r = await uploadFile(file, 'files', {
          token,
          userId: user.id,
          projectId: String(project.id),
          assetType: 'file',
        });
        const url = r.url; const type = r.type;
        const isSafetensorsFile = /\.safetensors$/i.test(file.name);
        const scale = isSafetensorsFile ? (data.scale ?? 1) : 1;

        updateNodeData(id, {
          content: {
            url,
            name: file.name,
            type,
          },
          text: undefined,
          // safetensors 默认 scale=1（若之前已有值，则沿用）
          scale: isSafetensorsFile ? scale : undefined,
        });

        // 如果文件已上传，检查是否有已连接的图像节点，并写入lora信息
        if (url && (isSafetensorsFile || /\.zip$/i.test(file.name))) {
          writeLoraToConnectedImageNodes(url, file.name, scale);
        }
      }
  } catch (error) {
      handleError('Error uploading file', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handlePickFromLibrary = (item: LibraryPickResult) => {
    const url = item.rawSrc ?? item.src;
    if (!url) return;
    updateNodeData(id, {
      content: {
        url,
        name: item.name || undefined,
        type: item.contentType || undefined,
      },
      text: undefined,
      fileName: undefined,
      scale: undefined,
    });
  };

  const openLibraryPicker = () => {
    void requestLibraryPick().then((item) => {
      if (item) handlePickFromLibrary(item);
    });
  };

  const toolbar: React.ComponentProps<typeof NodeLayout>['toolbar'] = hasContent
    ? [
        {
          children: (
            <div className="flex items-center gap-2">
              <ReuploadButton
                disabled={isUploading}
                onSelectFiles={(files) => void handleDrop(files)}
                accept=".json,.safetensors,.zip,application/json,application/octet-stream,application/zip,application/x-zip-compressed"
                aria-label={t('Re-upload')}
              />
            </div>
          ),
        },
      ]
    : undefined;

  const bgStyle = { backgroundColor: 'var(--node-bg)' } as const;

  return (
    <NodeLayout
      id={id}
      data={data}
      type={type}
      title={title}
      variant="modern"
      toolbar={toolbar}
      collapsedAspectRatio="16/9"
      collapsedSummary={<FileCollapsedSummary data={data as any} />}
    >
      {isUploading && (
        <NodeSurface aspectRatio={'1/1'} bgStyle={bgStyle} showDashed={false}>
          <div className="relative flex h-full items-center justify-center p-6">
            <Loader2Icon size={24} className="size-6 animate-spin text-muted-foreground" />
          </div>
        </NodeSurface>
      )}
      {!isUploading && data.text && (
        <NodeSurface aspectRatio={'1/1'} bgStyle={bgStyle} showDashed={false}>
          <div className="flex h-full flex-col justify-center gap-2 p-4">
            {data.fileName && (
              <div className="flex items-center gap-2">
                <FileIcon size={16} className="text-muted-foreground" />
                <span className="truncate font-medium text-sm">{data.fileName}</span>
              </div>
            )}
            <div className="flex items-center gap-2 rounded border p-4 bg-muted/50">
              <FileIcon size={16} className="text-muted-foreground" />
              <span className="text-sm text-muted-foreground">{t('JSON file loaded and ready to use')}</span>
            </div>
          </div>
        </NodeSurface>
      )}
      {!isUploading && data.content && (
        <NodeSurface aspectRatio={'1/1'} bgStyle={bgStyle} showDashed={false}>
          <div className="relative flex h-full items-center justify-center p-4">
            <FilePreview {...data.content} />
          </div>
        </NodeSurface>
      )}
      {!isUploading && !data.text && !data.content && (
        <NodeSurface aspectRatio={'1/1'} bgStyle={bgStyle} showDashed={false}>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,.safetensors,.zip"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files;
                if (f?.length) void handleDrop(Array.from(f));
                e.target.value = '';
              }}
            />
            <div className="flex flex-col w-full max-w-[220px]">
              <button
                type="button"
                className={EMPTY_MENU_ITEM_CN}
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload size={18} className="text-muted-foreground/70 shrink-0" />
                <span>{t('Upload from local')}</span>
              </button>
              <button
                type="button"
                className={EMPTY_MENU_ITEM_CN}
                onClick={openLibraryPicker}
              >
                <FolderOpen size={18} className="text-muted-foreground/70 shrink-0" />
                <span>{t('Pick from library')}</span>
              </button>
            </div>
          </div>
        </NodeSurface>
      )}
      {data.content && isSafetensors && (
        <div className="px-3 pb-3">
          <div className="flex w-full items-center justify-center gap-2">
            <Label htmlFor={`${id}-lora-scale`}>Lora Scale</Label>
            <Input
              id={`${id}-lora-scale`}
              type="number"
              min={0}
              max={4}
              step={0.01}
              value={data.scale ?? 1}
              onChange={(e) => {
                const next = parseFloat(e.target.value);
                updateNodeData(id, { scale: Number.isNaN(next) ? undefined : next });
              }}
              className="h-8 w-32"
            />
          </div>
        </div>
      )}
    </NodeLayout>
  );
};
